// ./src/pages/WPPluginsPage.js
import React, { useState, useEffect } from 'react';
import { 
  ExternalLink, 
  Sparkles, 
  Image, 
  Download, 
  TestTube,
  Github,
  Layers,
  Zap,
  RefreshCw,
  FileCode,
  Bot
} from 'lucide-react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useOpenGraph from '../hooks/useOpenGraph';
import { fetchWithCache } from '../utils/githubCache';

const basePlugins = [
  {
    name: 'Superdraft',
    repoName: 'superdraft',
    tagline: 'AI Toolkit for WordPress',
    description: 'A comprehensive AI writing assistant that enhances your content creation workflow. Smart autocomplete, intelligent tagging, writing recommendations, and image generation—all integrated seamlessly into the WordPress editor.',
    features: [
      'Smart Compose: Gmail-style inline completions as you type',
      'AI-powered tag & category suggestions with bulk processing',
      'Real-time writing tips and SEO recommendations',
      'AI image generation & editing with Nano Banana',
      'Customizable prompts with template syntax'
    ],
    icon: Sparkles,
    gradient: 'bg-gradient-to-r from-purple-500 to-pink-500',
    github: 'https://github.com/WP-Autoplugin/superdraft',
    stars: 15,
    tags: ['AI', 'Writing', 'Autocomplete', 'SEO', 'BYOK']
  },
  {
    name: 'WP Nano Banana',
    repoName: 'wp-banana',
    tagline: 'AI Image Generation & Editing',
    description: 'Generate and edit images with AI directly in your WordPress Media Library. Supports 20+ state-of-the-art models including Gemini, GPT-Image, FLUX, and Imagen. Native integration that feels like part of WordPress core.',
    features: [
      'Text-to-image generation from simple prompts',
      'Edit existing images with AI (style transfer, background removal)',
      'Combine multiple images into unique composites',
      'Works in Gutenberg, Classic Editor, Elementor & WooCommerce',
      'Non-destructive editing with full undo history'
    ],
    icon: Image,
    gradient: 'bg-gradient-to-r from-yellow-400 to-orange-500',
    github: 'https://github.com/WP-Autoplugin/wp-banana',
    stars: 66,
    tags: ['AI', 'Images', 'Media Library', 'Generation', 'Editing']
  },
  {
    name: 'Mockomatic',
    repoName: 'mockomatic',
    tagline: 'AI Demo Content Generator',
    description: 'Instantly scaffold realistic demo sites with AI-generated posts, pages, categories, tags, and featured images. Perfect for prototypes, staging sites, and theme demos. One click to a fully populated WordPress site.',
    features: [
      'Generate posts & pages with rich Gutenberg blocks',
      'Auto-create categories, tags & featured images',
      'Supports OpenAI, Gemini for text; Replicate for images',
      'Live progress tracking with stop/resume capability',
      'Custom prompts for tone, language & domain'
    ],
    icon: TestTube,
    gradient: 'bg-gradient-to-r from-emerald-500 to-teal-500',
    github: 'https://github.com/WP-Autoplugin/mockomatic',
    stars: 4,
    tags: ['AI', 'Demo Content', 'Staging', 'Prototyping']
  },
  {
    name: 'LLMs.txt for WP',
    repoName: 'llms-txt-for-wp',
    tagline: 'Make Your Site AI-Friendly',
    description: 'Implement the llms.txt standard to help Large Language Models understand your content. Generate machine-readable content summaries and Markdown versions of posts—like robots.txt, but for AI.',
    features: [
      'Auto-generate llms.txt with customizable content',
      'Markdown versions of posts via .md URL extension',
      'Control which content types are included',
      'Help LLMs discover and understand your site',
      'Fully translatable with filter hooks'
    ],
    icon: FileCode,
    gradient: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    github: 'https://github.com/WP-Autoplugin/llms-txt-for-wp',
    stars: 27,
    tags: ['AI', 'SEO', 'LLM', 'Markdown', 'Discovery']
  },
  {
    name: 'hub2wp',
    repoName: 'hub2wp',
    tagline: 'GitHub Plugins for WordPress',
    description: 'Discover, install, and update plugins hosted on GitHub directly from your WordPress dashboard. Thousands of quality plugins beyond WordPress.org are just a click away—with automatic update notifications.',
    features: [
      'Search & browse GitHub plugin repositories',
      'One-click install for any compatible plugin',
      'Automatic update notifications & management',
      'Built-in caching to minimize API requests',
      'Optional GitHub token for higher rate limits'
    ],
    icon: Download,
    gradient: 'bg-gradient-to-r from-gray-700 to-gray-900',
    github: 'https://github.com/WP-Autoplugin/hub2wp',
    stars: 31,
    tags: ['GitHub', 'Plugin Management', 'Updates', 'Developer']
  },
  {
    name: 'WP-Autoplugin',
    repoName: 'wp-autoplugin',
    tagline: 'AI-Powered Plugin Generator',
    description: 'Generate, fix, and extend WordPress plugins using AI. Create functional plugins from simple descriptions, extend third-party plugins with hooks, and fix bugs automatically right from your WordPress dashboard.',
    features: [
      'Generate plugins from descriptions using 30+ AI models',
      'Extend any plugin or theme by analyzing available hooks',
      'Auto-detect and fix fatal errors with AI',
      'Specialized models for planning, coding & reviewing'
    ],
    icon: Bot,
    gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600',
    github: 'https://github.com/WP-Autoplugin/wp-autoplugin',
    stars: 366,
    tags: ['AI', 'Plugin Generator', 'WordPress', 'Development', 'BYOK']
  }
];

const PluginCard = ({ plugin }) => {
  const { 
    name, 
    tagline, 
    description, 
    features, 
    icon: Icon, 
    gradient, 
    github, 
    stars,
    tags 
  } = plugin;

  return (
    <div className="group relative bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      {/* Gradient Header */}
      <div className={`h-2 ${gradient}`}></div>
      
      <div className="p-6">
        {/* Icon & Title */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${gradient}`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">{name}</h3>
              <p className="text-sm text-gray-500">{tagline}</p>
            </div>
          </div>
          {stars && (
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
              </svg>
              {stars}
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-4 leading-relaxed">
          {description}
        </p>

        {/* Features List */}
        <ul className="space-y-2 mb-5">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
              <Zap className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-5">
          {tags.map((tag, index) => (
            <span 
              key={index} 
              className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <a
            href={github}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Github className="w-4 h-4" />
            View on GitHub
          </a>
        </div>
      </div>
    </div>
  );
};

const WPPluginsPage = () => {
  useDocumentTitle('WP-Autoplugin - Free WordPress Plugins');
  useOpenGraph({
    title: 'WP-Autoplugin - Free WordPress Plugins',
    description: 'A collection of free, open-source WordPress plugins. AI-powered content creation, image generation, demo content scaffolding, and more.',
  });

  const [plugins, setPlugins] = useState(basePlugins);

  useEffect(() => {
    const fetchGitHubData = async () => {
      try {
        const repos = await fetchWithCache('https://api.github.com/users/WP-Autoplugin/repos');
        
        // Create a map of repo data by repo name
        const repoMap = {};
        repos.forEach(repo => {
          repoMap[repo.name] = {
            stars: repo.stargazers_count,
            topics: repo.topics || []
          };
        });

        // Update plugins with live data
        const updatedPlugins = basePlugins.map(plugin => {
          const repoData = repoMap[plugin.repoName];
          if (repoData) {
            return {
              ...plugin,
              stars: repoData.stars,
              // Only update tags if topics exist, otherwise keep base tags
              // tags: repoData.topics.length > 0 ? repoData.topics : plugin.tags
            };
          }
          return plugin;
        });

        setPlugins(updatedPlugins);
      } catch (error) {
        console.error('Failed to fetch GitHub data:', error);
        // Keep base data on error
      }
    };

    fetchGitHubData();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 opacity-70"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-sm font-medium text-gray-600 mb-6 shadow-sm">
              <Layers className="w-4 h-4 text-blue-500" />
              Free & Open Source WordPress Plugins
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
              A Collection of
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600"> Free WordPress Plugins</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Powerful, AI-enhanced tools to supercharge your WordPress workflow.
              All plugins are free, open-source, and built with the BYOK philosophy.
            </p>
            
            {/* Stats */}
            <div className="flex justify-center gap-8 mt-10">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">{plugins.length}</div>
                <div className="text-sm text-gray-500">Plugins</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">100%</div>
                <div className="text-sm text-gray-500">Free & Open Source</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">BYOK</div>
                <div className="text-sm text-gray-500">No Middleman Fees</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plugins Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plugins.map((plugin, index) => (
            <PluginCard key={index} plugin={plugin} />
          ))}
        </div>
      </div>

      {/* BYOK Philosophy Section */}
      <div className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">
                The BYOK Philosophy
              </h2>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                These AI-powered plugins follow the <strong>Bring Your Own Key</strong> approach.
                Connect directly to AI providers using your own API keys—no middleman fees,
                no subscriptions, no hidden costs.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-gray-300">Full control over your AI usage and costs</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-gray-300">Support for multiple AI providers</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-gray-300">Works with custom & self-hosted models</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-gray-300">No account required—just install and configure</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
                  <RefreshCw className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Stay Updated</h3>
                <p className="text-gray-400 mb-6">
                  Follow the GitHub organization to get notified about new plugins, updates, and features.
                </p>
                <a
                  href="https://github.com/WP-Autoplugin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Github className="w-5 h-5" />
                  Follow WP-Autoplugin
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Want to Contribute?
          </h2>
          <p className="text-blue-100 text-lg mb-6 max-w-2xl mx-auto">
            All plugins are open source and welcome contributions. Report bugs, suggest features,
            or submit pull requests on GitHub.
          </p>
          <a
            href="https://github.com/WP-Autoplugin?tab=repositories"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
          >
            <Github className="w-5 h-5" />
            View All Repositories
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default WPPluginsPage;
